/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { SortDirection } from "@atg/icf-react-components/utils/sortTable";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError, isAxiosError } from "axios";

import { ApiErrorCode } from "~/constants";
import MySitesApi from "~/features/mySites/MySitesApi";
import mySitesApiTransforms from "~/features/mySites/transformers/mySitesApiTransforms";
import transformToPromotedPrograms from "~/features/mySites/transformers/transformToPromotedPrograms";
import {
  GetPostbackListRequestBody,
  MySitesSiteList,
  PostAddPostbackRequestBody,
  PostbackDetails,
  PostCreateBlackLabelSiteRequestBody,
  PostCreateBlackLabelSiteResponse,
  PostCreatePromotionPayload,
  PostCreateSiteDraftPayload,
  PostDeletePostbackRequestBody,
  PostEditPostbackRequestBody,
  PostValidatePostbackRequestBody,
  PostValidateWhitelabelDomainPayload,
} from "~/features/mySites/types";
import { RootState } from "~/state/types";
import { CheckUrlErrorResult, WebserviceResponse } from "~/types";
import convertToBase64 from "~/utils/convertToBase64";
import createThunkError from "~/utils/createThunkError";
import getApiErrorCodeFromResponse from "~/utils/errors/getApiErrorCodeFromResponse";

// MY SITES
export const loadMySitesAsync = createAsyncThunk("mySites/loadMySites", async (_) => {
  const siteListPromise = MySitesApi.getSiteList();
  const sitePromotionListPromise = MySitesApi.getSitePromotionList();
  const blackLabelSiteReviewListPromise = MySitesApi.getListBLackLabelSiteReview();

  const [siteListResponse, sitePromotionListResponse, blackLabelSiteReviewListResponse] = await Promise.all([
    siteListPromise,
    sitePromotionListPromise,
    blackLabelSiteReviewListPromise,
  ]);

  const siteList = mySitesApiTransforms.siteListToMySitesList(siteListResponse.data.result);
  const sitePromotionList = mySitesApiTransforms.sitePromotionListToMySitesList(sitePromotionListResponse.data.result);
  const blackLabelSiteReviewList = mySitesApiTransforms.blackLabelSiteReviewListToMySitesList(
    blackLabelSiteReviewListResponse.data.result?.items ?? [],
  );

  const mySitesSiteList: MySitesSiteList = [...siteList, ...sitePromotionList, ...blackLabelSiteReviewList];
  const totalSites = mySitesSiteList.length;

  return {
    totalSites,
    siteList: mySitesSiteList,
  };
});

// CREATE WHITE LABEL
export const postValidateWhitelabelDomainAsync = createAsyncThunk(
  "mySites/postValidateWhitelabelDomain",
  async (payload: PostValidateWhitelabelDomainPayload, thunkAPI) => {
    const validateRes = await MySitesApi.postValidateWhitelabelDomain(payload);

    const apiErrorCode = getApiErrorCodeFromResponse(validateRes);
    if (apiErrorCode) {
      return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode }));
    }

    return {
      domain: payload.domain,
    };
  },
);

export const postCreateNewSiteDraftAsync = createAsyncThunk(
  "mySites/postCreateNewSiteDraft",
  async (payload: PostCreateSiteDraftPayload, thunkAPI) => {
    try {
      const createRes = await MySitesApi.postCreateSiteDraft(payload);
      const apiErrorCode = getApiErrorCodeFromResponse(createRes);
      if (apiErrorCode) {
        return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode, message: createRes.data.error }));
      }

      return {
        newReviewSiteId: createRes.data.data.id,
        payProgram: String(payload.payoutType),
        domain: payload.url,
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        return thunkAPI.rejectWithValue(createThunkError(error.response?.data as Record<string, string>));
      }

      throw error;
    }
  },
);

export const convertWhitelabelLogoAsync = createAsyncThunk(
  "mySites/convertWhitelabelLogoAsync",
  async (payload: { file: File }, thunkAPI) => {
    try {
      return await convertToBase64(payload.file);
    } catch (error) {
      return thunkAPI.rejectWithValue(createThunkError(error as Error));
    }
  },
);

// PROMOTE SITE
export const loadPromotionSitesAsync = createAsyncThunk("sitePromo/loadPromotionSites", async () => {
  const response = await MySitesApi.getPromotionSites();

  return transformToPromotedPrograms(response.data);
});

export const postCreatePromotionAsync = createAsyncThunk(
  "sitePromo/postCreatePromotion",
  async (payload: PostCreatePromotionPayload, thunkAPI) => {
    const response = await MySitesApi.postCreatePromotion(payload);

    if (!response.data.success) {
      const error = response.data.error ?? "Failed to create site";
      return thunkAPI.rejectWithValue(createThunkError({ message: error }));
    }

    return payload.programtype;
  },
);

export const getPostbackSitesAsync = createAsyncThunk("postback/getSites", async () => {
  const siteListPromise = MySitesApi.getSiteList();
  const sitePromotionListPromise = MySitesApi.getSitePromotionList();
  const [siteListResponse, sitePromotionListResponse] = await Promise.all([siteListPromise, sitePromotionListPromise]);
  const siteList = mySitesApiTransforms.siteListToPostbackSiteList(siteListResponse.data.result);
  const sitePromotionList = mySitesApiTransforms.sitePromotionListToPostbackSiteList(
    sitePromotionListResponse.data.result,
  );

  return {
    sites: siteList,
    promotions: sitePromotionList,
  };
});

export const getPostbackListAsync = createAsyncThunk(
  "postback/getList",
  async (payload: GetPostbackListRequestBody) => {
    const response = await MySitesApi.getPostbackList(payload);

    const postbacks: PostbackDetails[] = response.data.result.items;
    const totalPages = response.data.result.totalPages;

    return {
      postbacks,
      totalPages,
    };
  },
);

const refreshPostbackList = createAsyncThunk("postback/refreshList", async (_, thunkApi) => {
  const state = thunkApi.getState() as RootState;
  const pagination = state.mySites.postback.data.pagination;
  const resellerId = state.auth.account?.resellerId;

  const requestBody: GetPostbackListRequestBody = {
    resellerId: resellerId ?? 0,
    page: pagination.currentPageIndex + 1,
    limit: 15,
    orderByDirection: pagination.sortDirection === SortDirection.ASCEND ? "ASC" : "DESC",
    orderByField: pagination.sortedColumn,
  };

  return await thunkApi.dispatch(getPostbackListAsync(requestBody));
});

export const postAddPostbackAsync = createAsyncThunk(
  "postback/addPostback",
  async (payload: PostAddPostbackRequestBody, thunkApi) => {
    try {
      await MySitesApi.postAddPostback(payload);
      await thunkApi.dispatch(refreshPostbackList());
    } catch (error) {
      if (isAxiosError(error)) {
        const response = error.response?.data as WebserviceResponse<CheckUrlErrorResult>;
        const message = response.error;
        const errorCode = response?.result.errorCode as ApiErrorCode;
        return thunkApi.rejectWithValue(createThunkError({ apiErrorCode: errorCode ?? 400, message }));
      }
      return thunkApi.rejectWithValue(createThunkError({ apiErrorCode: 500, message: "unknown error" }));
    }
  },
);

export const postEditPostbackAsync = createAsyncThunk(
  "postback/editPostback",
  async (payload: PostEditPostbackRequestBody, thunkApi) => {
    try {
      await MySitesApi.postEditPostback(payload);
      await thunkApi.dispatch(refreshPostbackList());
    } catch (error) {
      if (isAxiosError(error)) {
        const response = error.response?.data as WebserviceResponse<CheckUrlErrorResult>;
        const message = response.error;
        const errorCode = response.result.errorCode as ApiErrorCode;
        return thunkApi.rejectWithValue(createThunkError({ apiErrorCode: errorCode ?? 400, message }));
      }
      return thunkApi.rejectWithValue(createThunkError({ apiErrorCode: 500, message: "unknown error" }));
    }
  },
);

export const postDeletePostbackAsync = createAsyncThunk(
  "postback/deletePostback",
  async (payload: PostDeletePostbackRequestBody, thunkApi) => {
    await MySitesApi.postDeletePostback(payload);

    await thunkApi.dispatch(refreshPostbackList());

    return true;
  },
);

export const postValidatePostbackAsync = createAsyncThunk(
  "postback/validatePostback",
  async (payload: PostValidatePostbackRequestBody) => {
    await MySitesApi.postValidatePostback(payload);

    return true;
  },
);

export const postCreateBlackLabelSiteReviewAsync = createAsyncThunk(
  "mySites/postCreateBlackLabelSiteReviewAsync",
  async (payload: PostCreateBlackLabelSiteRequestBody, thunkAPI) => {
    try {
      const res = await MySitesApi.postCreateBLackLabelSiteReview(payload);

      const apiErrorCode = getApiErrorCodeFromResponse(res);
      if (apiErrorCode) {
        return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode, message: res.data.message }));
      }
      return true;
    } catch (error) {
      if (isAxiosError(error)) {
        const response = error.response?.data as PostCreateBlackLabelSiteResponse;
        let message = response.message ?? "unknown error";

        if (response.errors?.[0].error) {
          message = response.errors?.[0].error;
        }

        const errorCode = error.response?.status;
        return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode: errorCode ?? 400, message }));
      }
      return thunkAPI.rejectWithValue(createThunkError({ apiErrorCode: 500, message: "unknown error" }));
    }
  },
);

export const getTimezonesAsync = createAsyncThunk("mySites/getTimezonesAsync", async () => {
  const res = await MySitesApi.getTimezones();

  return res.data.result;
});
